import {makeObservable, observable, runInAction} from "mobx";
import {IResidentAdminService} from "../../../services/residentAdminService";
import {DepartmentTypeCode} from "../../../shared/enums/departmentTypeCode";

/**
 * The interface for ResidentActionRequiredCountsDomainStore.
 */
export interface IResidentActionRequiredCountsDomainStore {
  independent: number;
  servicedApartment: number;

  /**
   * Loads the counts for the given village.
   */
  loadCounts: (villageId: string) => Promise<void>;

  /**
   * Sets one department's count from a resident list that has just been loaded, so the badge matches that page.
   */
  setDepartmentCount: (
    villageId: string,
    departmentTypeCode: string,
    count: number
  ) => void;
}

/**
 * The number of action required residents on each resident admin list.
 */
export class ResidentActionRequiredCountsDomainStore
  implements IResidentActionRequiredCountsDomainStore
{
  private readonly residentAdminService: IResidentAdminService;
  private requestVersion: number = 0;
  private currentVillageId?: string;
  // Departments set from a loaded list since the current load started; that load must not overwrite them.
  private departmentsSetFromList = new Set<string>();
  independent: number = 0;
  servicedApartment: number = 0;

  constructor(residentAdminService: IResidentAdminService) {
    makeObservable(this, {
      independent: observable,
      servicedApartment: observable
    });
    this.residentAdminService = residentAdminService;
  }

  loadCounts = async (villageId: string) => {
    const version = ++this.requestVersion;
    this.departmentsSetFromList.clear();
    if (villageId !== this.currentVillageId) {
      runInAction(() => {
        this.independent = 0;
        this.servicedApartment = 0;
      });
    }
    this.currentVillageId = villageId;

    let counts = {independent: 0, servicedApartment: 0};
    try {
      const response = await this.residentAdminService.getResidentActions(
        villageId
      );
      counts = {
        independent: response?.counts?.independent ?? 0,
        servicedApartment: response?.counts?.serviced ?? 0
      };
    } catch {
      // The badge is informational; don't interrupt the user if it fails.
    }

    if (version !== this.requestVersion) {
      return;
    }

    runInAction(() => {
      if (!this.departmentsSetFromList.has(DepartmentTypeCode.Independent)) {
        this.independent = counts?.independent ?? 0;
      }
      if (!this.departmentsSetFromList.has(DepartmentTypeCode.Serviced)) {
        this.servicedApartment = counts?.servicedApartment ?? 0;
      }
      this.departmentsSetFromList.clear();
    });
  };

  setDepartmentCount = (
    villageId: string,
    departmentTypeCode: string,
    count: number
  ) => {
    if (villageId !== this.currentVillageId) {
      return;
    }

    runInAction(() => {
      if (departmentTypeCode === DepartmentTypeCode.Independent) {
        this.independent = count;
      } else if (departmentTypeCode === DepartmentTypeCode.Serviced) {
        this.servicedApartment = count;
      } else {
        return;
      }
      this.departmentsSetFromList.add(departmentTypeCode);
    });
  };
}
